import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LockIcon from '@mui/icons-material/Lock';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiFetch } from './api';
import { 
  registerWebAuthn, 
  setupTOTP, 
  verifyTOTPSetup, 
  isWebAuthnSupported 
} from './webauthn';
import ChangePasswordDialog from './ChangePasswordDialog';

function UserProfile({ user, onBack }) {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // TOTP setup state
  const [totpSetupOpen, setTotpSetupOpen] = useState(false);
  const [totpQRCode, setTotpQRCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  
  // Password change dialog
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/auth/credentials');
      setCredentials(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Anmeldedaten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCredentials();
  }, []);

  const handleAddWebAuthn = async () => {
    if (!isWebAuthnSupported()) {
      setError('WebAuthn wird von Ihrem Browser nicht unterstützt.');
      return;
    }

    try {
      setError(null);
      await registerWebAuthn(user.username);
      setSuccess('WebAuthn-Schlüssel erfolgreich hinzugefügt!');
      await loadCredentials();
    } catch (err) {
      console.error('WebAuthn registration error:', err);
      setError(err.message || 'Fehler beim Hinzufügen des WebAuthn-Schlüssels');
    }
  };

  const handleDeleteCredential = async () => {
    if (!credentialToDelete) return;

    try {
      await apiFetch(`/auth/credentials/${credentialToDelete}`, {
        method: 'DELETE'
      });
      setSuccess('Schlüssel erfolgreich gelöscht!');
      await loadCredentials();
      setDeleteDialogOpen(false);
      setCredentialToDelete(null);
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen des Schlüssels');
      setDeleteDialogOpen(false);
    }
  };

  const handleSetupTOTP = async () => {
    try {
      setError(null);
      const data = await setupTOTP(user.username);
      setTotpQRCode(data.qr_code);
      setTotpSecret(data.secret);
      setTotpSetupOpen(true);
    } catch (err) {
      setError(err.message || 'Fehler beim Einrichten von TOTP');
    }
  };

  const handleVerifyTOTP = async () => {
    try {
      setError(null);
      await verifyTOTPSetup(totpCode);
      setSuccess('TOTP erfolgreich eingerichtet!');
      setTotpSetupOpen(false);
      setTotpQRCode(null);
      setTotpSecret(null);
      setTotpCode('');
      await loadCredentials();
    } catch (err) {
      setError(err.message || 'Ungültiger Code');
    }
  };

  const handleDisableTOTP = async () => {
    try {
      setError(null);
      await apiFetch('/totp/disable', { method: 'POST' });
      setSuccess('TOTP wurde deaktiviert!');
      await loadCredentials();
    } catch (err) {
      setError(err.message || 'Fehler beim Deaktivieren von TOTP');
    }
  };

  const hasMFA = credentials 
    ? (credentials.webauthn_credentials.length > 0 || credentials.totp_enabled)
    : false;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Benutzerprofil
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* User Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Benutzerinformationen
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography>
              <strong>Benutzername:</strong> {user.username}
            </Typography>
            {user.admin && <Chip label="Admin" color="primary" size="small" />}
          </Stack>

          {user.admin && !hasMFA && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
              <strong>MFA erforderlich!</strong> Admin-Konten müssen Multi-Faktor-Authentifizierung aktiviert haben.
              Bitte richten Sie WebAuthn oder TOTP ein.
            </Alert>
          )}

          {user.admin && hasMFA && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
              MFA ist aktiviert. Ihr Admin-Konto ist gesichert.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* WebAuthn Credentials */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <FingerprintIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              WebAuthn Schlüssel
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddWebAuthn}
              disabled={!isWebAuthnSupported()}
              size="small"
            >
              Hinzufügen
            </Button>
          </Box>

          {!isWebAuthnSupported() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              WebAuthn wird von Ihrem Browser nicht unterstützt.
            </Alert>
          )}

          {credentials?.webauthn_credentials.length > 0 ? (
            <List>
              {credentials.webauthn_credentials.map((cred, idx) => (
                <React.Fragment key={cred.id}>
                  <ListItem>
                    <ListItemText
                      primary={`Schlüssel ${idx + 1}`}
                      secondary={`ID: ${cred.id_display} | Erstellt: ${cred.created_at}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setCredentialToDelete(cred.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < credentials.webauthn_credentials.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Keine WebAuthn-Schlüssel konfiguriert
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* TOTP Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <PhoneAndroidIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              TOTP (Authenticator App)
            </Typography>
            {credentials?.totp_enabled ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisableTOTP}
                size="small"
              >
                Deaktivieren
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleSetupTOTP}
                size="small"
              >
                Einrichten
              </Button>
            )}
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>
              Status:
            </Typography>
            {credentials?.totp_enabled ? (
              <Chip label="Aktiviert" color="success" size="small" icon={<CheckCircleIcon />} />
            ) : (
              <Chip label="Nicht aktiviert" size="small" />
            )}
          </Stack>

          {credentials?.totp_enabled && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sie können sich mit Ihrer Authenticator-App (z.B. Google Authenticator, Authy) anmelden.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Password Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              <LockIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Passwort
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setChangePasswordOpen(true)}
              size="small"
            >
              Ändern
            </Button>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>
              Status:
            </Typography>
            {credentials?.password_set ? (
              <Chip label="Gesetzt" color="success" size="small" icon={<CheckCircleIcon />} />
            ) : (
              <Chip label="Nicht gesetzt" size="small" />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={onBack}>
          Zurück
        </Button>
      </Box>

      {/* TOTP Setup Dialog */}
      <Dialog open={totpSetupOpen} onClose={() => setTotpSetupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>TOTP Einrichten</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Scannen Sie den QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, Authy):
          </DialogContentText>
          
          {totpQRCode && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img src={totpQRCode} alt="TOTP QR Code" style={{ maxWidth: '100%' }} />
            </Box>
          )}

          {totpSecret && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Geheimer Schlüssel:</strong> {totpSecret}
              <br />
              <Typography variant="caption">
                Bewahren Sie diesen Schlüssel sicher auf, falls Sie den QR-Code nicht scannen können.
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            label="Bestätigungscode"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            placeholder="123456"
            helperText="Geben Sie den 6-stelligen Code aus Ihrer App ein"
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTotpSetupOpen(false)}>Abbrechen</Button>
          <Button onClick={handleVerifyTOTP} variant="contained" disabled={totpCode.length !== 6}>
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Credential Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Schlüssel löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie diesen Sicherheitsschlüssel wirklich löschen?
            {user.admin && credentials?.webauthn_credentials.length === 1 && !credentials?.totp_enabled && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Warnung:</strong> Als Admin benötigen Sie mindestens eine MFA-Methode.
                Richten Sie zuerst TOTP ein, bevor Sie Ihren letzten WebAuthn-Schlüssel löschen.
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteCredential} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSuccess={() => {
          setSuccess('Passwort erfolgreich geändert!');
          setChangePasswordOpen(false);
          loadCredentials();
        }}
      />
    </Box>
  );
}

export default UserProfile;
